import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * A modal with the review confirmations' look. Escape closes it unless `busy`. Each `view`
 * (default: the title) starts on its enabled `data-autofocus` control, else its first enabled
 * button, else the dialog itself; on close, focus goes back to whatever had it before the dialog
 * opened, if that is still on the page.
 */
export function Dialog({ title, view = title, busy = false, onClose, children }: {
  title: string
  view?: string
  busy?: boolean
  onClose: () => void
  children: ReactNode
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const id = useId()
  const latest = useRef({ busy, onClose })
  latest.current = { busy, onClose }
  useEffect(() => {
    const element = dialog.current!
    const previous = document.activeElement as HTMLElement | null
    element.showModal()
    return () => { element.close(); if (previous?.isConnected) previous.focus() }
  }, [])
  useEffect(() => {
    const element = dialog.current!
    const target = element.querySelector<HTMLElement>('[data-autofocus]:not(:disabled)')
      ?? element.querySelector<HTMLElement>('button:not(:disabled)') ?? element
    target.focus()
  }, [view])
  return createPortal(<dialog ref={dialog} tabIndex={-1} className="review-confirmation" aria-labelledby={`${id}-title`}
    onCancel={(event) => { event.preventDefault(); if (!busy) onClose() }}
    // Browsers may close a modal on a repeated Escape despite preventDefault: reopen it while busy,
    // otherwise let the owner unmount it so the next open starts a fresh dialog.
    onClose={(event) => {
      const element = event.currentTarget
      if (!element.isConnected || element.open) return
      if (latest.current.busy) element.showModal()
      else latest.current.onClose()
    }}>
    <h2 id={`${id}-title`}>{title}</h2>
    {children}
  </dialog>, document.body)
}
